import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { manualBlogs } from "../core/api/data";
import { fetchAllArticles } from "../core/api/rss";
import useNews from "../core/hooks/useNews";
import { formatDate, getExcerpt } from "../core/utils/blog";
import Header from "../components/shared/header";
import FeaturedSlider from "../components/blog/FeaturedSlider";

const DISPLAY_FONT =
  "'Anton', 'Oswald', 'Arial Narrow', sans-serif";

const LoadingState = () => (
  <div className="flex min-h-[320px] flex-col items-center justify-center border border-[#2A2A2A] bg-[#0A0A0A] p-8 text-center">
    <div className="mb-6 flex gap-1.5">
      <span className="h-8 w-2 animate-pulse bg-[#D0182B]" />
      <span className="h-8 w-2 animate-pulse bg-[#D0182B] [animation-delay:150ms]" />
      <span className="h-8 w-2 animate-pulse bg-[#D0182B] [animation-delay:300ms]" />
    </div>

    <p style={{ fontFamily: DISPLAY_FONT }} className="text-lg tracking-wide text-[#EDEAE1]">
      LOADING THE FEED
    </p>
  </div>
);

const EmptyState = () => (
  <div
    className="flex min-h-[320px] flex-col items-center justify-center gap-2 border border-[#2A2A2A] bg-[#0A0A0A] p-8 text-center">
    <p style={{ fontFamily: DISPLAY_FONT }} className="text-xl text-[#EDEAE1]">
      NO REPORTS
    </p>
    <p className="text-[#8C8880]">Check back once the next one drops.</p>
  </div>
);

const ArticleCard = ({ article }) => {
  return (
    <article className="group flex flex-col overflow-hidden border border-[#2A2A2A] bg-[#0A0A0A] transitionAll hover:border-[#D0182B]">
      {article?.imageUrl && (
        <div className="relative aspect-[16/9] overflow-hidden pointer-events-none">
          <img src={article?.imageUrl} alt={article.title} loading="lazy"
            className=" absolute inset-0 h-full w-full object-cover grayscale transition-[filter] transitionAll group-hover:grayscale-0" />
        </div>
      )}

      <div className="flex flex-1 flex-col p-6">
        <h3 style={{ fontFamily: DISPLAY_FONT }} className="mb-3 text-xl uppercase leading-tight tracking-tight text-[#EDEAE1] pointer-events-none">
          {article.title}
        </h3>

        <p className="mb-6 flex-1 text-[0.9375rem] leading-relaxed text-[#8C8880] pointer-events-none">
          {article.excerpt}
        </p>

        <div className="flex items-center justify-between border-t border-[#2A2A2A] pt-4 text-sm">
          <span className="text-[#8C8880] pointer-events-none">{formatDate(article.publishedAt)}</span>
          <Link to={`/blog/${article?.slug}`} style={{ fontFamily: DISPLAY_FONT }}
            className="uppercase tracking-wide text-[#D0182B] group-hover:underline">
            Read Full Article
          </Link>
        </div>
      </div>
    </article>
  );
};


const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useNews(articles);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const data = await fetchAllArticles();

        const formattedArticles = data.map((article) => ({
          ...article,
          id: article.id || article._id,
          slug: article.slug,
          title: article.title,
          excerpt: article.description || getExcerpt(article.contentText),
          imageUrl: article.image || null,
          publishedAt: article.publishedDate,
          publisher: article.publisher,
          content: article.content,
          contentText: article.contentText,
          sourceUrl: article.sourceUrl,
          source: article.source,
        }));

        const sortedArticles = [...formattedArticles, ...manualBlogs].sort(
          (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
        );

        setArticles(sortedArticles);
      } catch (error) {
        console.error("Failed to load articles:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  const featuredArticles = articles.slice(0, 4);

  return (
    <main className="min-h-screen bg-[#0A0A0A] font-sans text-[#EDEAE1] pb-20">
      <Header />
      <div className="mx-auto">
        {loading && <LoadingState />}

        {!loading && error && <EmptyState />}

        {!loading && !error && (
          <>
            <FeaturedSlider blogs={featuredArticles} />

            <div className="mb-6 max-w-6xl mx-auto mt-12 flex items-center gap-4 pb-4 px-4 md:px-0">
              <h2
                style={{ fontFamily: DISPLAY_FONT }}
                className="text-2xl uppercase tracking-tight text-[#EDEAE1]"
              >
                Latest Reports
              </h2>
              <div className="h-0.5 flex-1 w-full bg-[#D0182B]" />
            </div>

            <section className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6 max-w-6xl mx-auto">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default Blog;
